import React from 'react';
import { isStaleChunkError, tryStaleChunkRecovery } from '../utils/stale-chunk-recovery';

// ============================================================================
// App-root React error boundary — v2, matching ConfirmModal's "quiet
// professional" language (2026-07-31).
// ----------------------------------------------------------------------------
// Renders the fallback on any render-time crash and — when the crash is a
// stale-chunk lazy-load failure after a mid-session deploy — shows an
// "updating" screen and auto-reloads once via the canonical
// stale-chunk-recovery helpers.
//
// WHY THIS WAS REWRITTEN
// The v1 screen was the consumer-alert cliché the ConfirmModal v5 rewrite was
// commissioned to remove: an 80px red badge over a centred column, a 2xl
// title, and body copy that apologised twice ("We're sorry, but something
// unexpected happened") without telling the operator a single useful thing.
// Peter asked for this screen to be brought into the same work.
//
// WHAT CHANGED, and why each one
//  · Left-aligned, 30px tinted tile, 16px glyph, hairline borders, keys on a
//    footer bar. Identical construction to ConfirmModal v5 so a crash looks
//    like the same product, not a browser error page.
//  · "QuoteMaker hit a problem", not "Something went wrong". The app ALREADY
//    has a house phrasing for this: AssistantErrorBoundary says "AI Assistant
//    hit a problem". The root boundary should not invent a second voice.
//  · The copy answers the question the operator actually has, which is not
//    "what happened" but "did I just lose my quote". It says plainly that
//    reloading deletes nothing and that already-saved work survives, and it
//    does NOT claim unsaved keystrokes are safe, because they are not.
//  · Version + timestamp are ON the screen. A crash report that says "it broke"
//    is unactionable; one that carries v5.47.45 and a time can be found in the
//    logs. "Copy details" puts the whole lot on the clipboard in one click.
//  · Semantic status red, never brand orange. docs/design-system.md: "Status
//    colours are not brand ... never follow --qm-accent."
//
// EVERYTHING HERE IS INLINE-STYLED AND SELF-CONTAINED ON PURPOSE. This is the
// one component that must render when the app is broken, so it imports no
// stylesheet class, no icon component and no config module. The glyphs are
// inline SVG for the same reason.
//
// TS-060 / DEC-COUNCIL-015 T2: extracted from App.jsx's module-scope prelude.
// Consumed by src/main.jsx, which wraps <App /> in <ErrorBoundary>.
// ============================================================================

const INK = '#1a1a1a';
// Verbatim from ConfirmModal v5's TONES.red / TONES.blue.
const RED = { text: '#b91c1c', tint: '#fef2f2', tintEdge: '#f6cfcf' };
const BLUE = { text: '#1d4ed8', tint: '#eff6ff', tintEdge: '#c3d9f7' };

const shell: React.CSSProperties = {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    background: '#f4f2ee',
    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
};

const card: React.CSSProperties = {
    width: '100%',
    maxWidth: '520px',
    background: '#fff',
    borderRadius: '12px',
    border: '1px solid #e6e1d9',
    boxShadow: '0 12px 32px rgba(26,26,26,0.14)',
    overflow: 'hidden',
};

function tile(tone: typeof RED): React.CSSProperties {
    return {
        width: '30px',
        height: '30px',
        flex: '0 0 auto',
        borderRadius: '8px',
        background: tone.tint,
        border: `1px solid ${tone.tintEdge}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: tone.text,
    };
}

const keyBase: React.CSSProperties = {
    height: '36px',
    padding: '0 14px',
    borderRadius: '7px',
    fontSize: '13.5px',
    fontWeight: 700,
    letterSpacing: '0.005em',
    cursor: 'pointer',
    minHeight: 0,
    lineHeight: 1,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
};

interface ErrorBoundaryProps {
    children: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
    recovering: boolean;
    copied: boolean;
    /** Stamped once on catch. Read at render time it would change every tick. */
    at: string;
}

export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null, recovering: false, copied: false, at: '' };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        if (isStaleChunkError(error)) {
            return { hasError: true, error, recovering: true };
        }
        return { hasError: true };
    }

    override componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        let at = '';
        try { at = new Date().toLocaleString('en-AU'); } catch { /* no Intl data */ }
        this.setState({ error, errorInfo, at });
        if (isStaleChunkError(error)) {
            tryStaleChunkRecovery(error);
        }
        if (typeof window !== 'undefined' && window.location.hostname !== 'localhost') {
            // Log error to analytics/monitoring service
        }
    }

    /** Version is read defensively: the crash may BE a bad module import. */
    private appVersion(): string {
        try {
            const w = window as any;
            return String(w.__QM_APP_VERSION__ || w.APP_VERSION || '').trim();
        } catch { return ''; }
    }

    private report(): string {
        return [
            'QuoteMaker error report',
            this.appVersion() ? `Version: ${this.appVersion()}` : '',
            this.state.at ? `Time: ${this.state.at}` : '',
            typeof window !== 'undefined' ? `Page: ${window.location.pathname}` : '',
            '',
            String(this.state.error || ''),
            String(this.state.errorInfo?.componentStack || ''),
        ].filter(Boolean).join('\n');
    }

    private copyDetails = (): void => {
        const text = this.report();
        const done = () => {
            this.setState({ copied: true });
            window.setTimeout(() => this.setState({ copied: false }), 2000);
        };
        try {
            if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(text).then(done, () => this.fallbackCopy(text, done));
                return;
            }
        } catch { /* fall through */ }
        this.fallbackCopy(text, done);
    };

    /** execCommand path, for a non-secure origin or a denied permission. */
    private fallbackCopy(text: string, done: () => void): void {
        try {
            const ta = document.createElement('textarea');
            ta.value = text;
            ta.setAttribute('readonly', '');
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
            done();
        } catch { /* nothing more to try; the details stay on screen to select */ }
    }

    override render() {
        if (!this.state.hasError) return this.props.children;

        // ── Stale-chunk auto-reload: not a failure, so it never reads as one ──
        if (this.state.recovering) {
            return (
                <div style={shell}>
                    <div style={{ ...card, maxWidth: '400px' }}>
                        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '18px' }}>
                            <div style={tile(BLUE)}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" aria-hidden="true">
                                    <path d="M21 12a9 9 0 1 1-6.2-8.6">
                                        <animateTransform attributeName="transform" type="rotate" from="0 12 12" to="360 12 12" dur="0.9s" repeatCount="indefinite" />
                                    </path>
                                </svg>
                            </div>
                            <div style={{ minWidth: 0 }}>
                                <h1 style={{ fontSize: '16px', fontWeight: 800, letterSpacing: '-0.005em', color: INK, margin: '3px 0 0', lineHeight: 1.3 }}>
                                    QuoteMaker updated
                                </h1>
                                <p style={{ fontSize: '13.5px', lineHeight: 1.55, color: '#4b5563', margin: '7px 0 0' }}>
                                    Loading the latest version. This takes a second.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            );
        }

        // ── The real crash ───────────────────────────────────────────────────
        const version = this.appVersion();
        return (
            <div style={shell}>
                <div style={card} role="alert" aria-labelledby="qm-eb-title">
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '18px 18px 16px' }}>
                        <div style={tile(RED)}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                <path d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0z" />
                                <path d="M12 9v4" />
                                <path d="M12 17h.01" />
                            </svg>
                        </div>
                        <div style={{ minWidth: 0, flex: '1 1 auto' }}>
                            <h1 id="qm-eb-title" style={{ fontSize: '16px', fontWeight: 800, letterSpacing: '-0.005em', color: INK, margin: '3px 0 0', lineHeight: 1.3 }}>
                                QuoteMaker hit a problem
                            </h1>
                            <p style={{ fontSize: '12px', fontWeight: 700, color: RED.text, margin: '4px 0 0', lineHeight: 1.4 }}>
                                This screen stopped drawing. Reloading almost always clears it.
                            </p>
                            <p style={{ fontSize: '13.5px', lineHeight: 1.55, color: '#4b5563', margin: '9px 0 0' }}>
                                Reloading deletes nothing. Work you had already saved will still be
                                there when you come back. Anything typed since your last save may
                                not have been kept.
                            </p>
                            {(version || this.state.at) && (
                                <p style={{ fontSize: '12px', lineHeight: 1.5, color: '#6b7280', margin: '8px 0 0', fontFamily: "'Roboto Mono', ui-monospace, monospace" }}>
                                    {[version && `v${version}`, this.state.at].filter(Boolean).join('  ·  ')}
                                </p>
                            )}
                        </div>
                    </div>

                    {this.state.error && (
                        <details style={{ borderTop: '1px solid #efece6' }}>
                            {/* The native marker is kept deliberately. This file
                                carries no stylesheet, so a custom chevron could
                                not be rotated on [open], and a summary with no
                                marker at all does not read as clickable. */}
                            <summary style={{ padding: '10px 18px 10px 34px', fontSize: '12px', fontWeight: 700, color: '#6b7280', cursor: 'pointer' }}>
                                What went wrong
                            </summary>
                            <pre style={{
                                margin: 0,
                                padding: '0 18px 14px',
                                fontSize: '11px',
                                lineHeight: 1.5,
                                color: '#991b1b',
                                fontFamily: "'Roboto Mono', ui-monospace, monospace",
                                whiteSpace: 'pre-wrap',
                                overflowWrap: 'anywhere',
                                maxHeight: '200px',
                                overflow: 'auto',
                            }}>
                                {String(this.state.error)}
                                {this.state.errorInfo?.componentStack}
                            </pre>
                        </details>
                    )}

                    <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                        gap: '8px',
                        padding: '12px 18px',
                        borderTop: '1px solid #efece6',
                        background: '#fbfaf7',
                    }}>
                        <button
                            type="button"
                            onClick={this.copyDetails}
                            style={{ ...keyBase, background: '#fff', color: '#374151', border: '1px solid #d8d2c8' }}
                        >
                            {this.state.copied ? 'Copied' : 'Copy details'}
                        </button>
                        <button
                            type="button"
                            onClick={() => window.location.reload()}
                            style={{ ...keyBase, background: '#dc2626', color: '#fff', border: '1px solid #b91c1c' }}
                        >
                            Reload QuoteMaker
                        </button>
                    </div>
                </div>
            </div>
        );
    }
}
